import React, { useState, useEffect, useRef, useCallback } from 'react';
import Parse from 'parse';
import {
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import 'tailwindcss/index.css';
import { GenesisHost } from '../genesis.shadow.dom';
import './LifeBot.css';

function ThinkingLoader() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.5,
        ml: 1,
        px: 1.5,
        py: 1,
        borderRadius: '18px',
        background:
          'linear-gradient(135deg, rgba(37, 99, 235, 0.14), rgba(56, 189, 248, 0.08))',
        border: '1px solid rgba(96, 165, 250, 0.22)',
        boxShadow: '0 12px 30px rgba(2, 6, 23, 0.26)',
      }}
    >
      <style>
        {`
          @keyframes lt-thinking-fill {
            0% { transform: translateY(34px); opacity: 0.45; }
            55% { transform: translateY(8px); opacity: 0.95; }
            100% { transform: translateY(-6px); opacity: 0.55; }
          }

          @keyframes lt-thinking-glow {
            0%, 100% { opacity: 0.35; transform: scale(0.96); }
            50% { opacity: 1; transform: scale(1.06); }
          }

          @keyframes lt-thinking-drift {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-2px); }
          }
        `}
      </style>

      <svg width="72" height="72" viewBox="0 0 72 72" aria-hidden="true">
        <defs>
          <linearGradient id="ltHeadStroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#67E8F9" />
            <stop offset="55%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#1D4ED8" />
          </linearGradient>
          <linearGradient id="ltHeadFill" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#1D4ED8" />
            <stop offset="45%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#67E8F9" />
          </linearGradient>
          <clipPath id="ltHeadClip">
            <path d="M44 8c8.8 0 16 6.8 16 15.6 0 4.1-1.7 7.8-4.2 10.6 1.1 1.8 1.7 3.6 1.7 5.7 0 4.7-3.2 8.8-7.6 10.2-.2 4.8-3.4 8.5-8.7 10.4-4.3 1.5-10.5 1.8-17.2.7 3.7-3.1 6-6.7 6.6-10.8-4.2-2.6-7.1-7.1-7.1-12.4 0-3.7 1.4-7 3.7-9.6-1.6-2.2-2.5-4.9-2.5-7.8C24.7 17.4 33.2 8 44 8Z" />
          </clipPath>
        </defs>

        <g style={{ animation: 'lt-thinking-drift 2.2s ease-in-out infinite' }}>
          <path
            d="M44 8c8.8 0 16 6.8 16 15.6 0 4.1-1.7 7.8-4.2 10.6 1.1 1.8 1.7 3.6 1.7 5.7 0 4.7-3.2 8.8-7.6 10.2-.2 4.8-3.4 8.5-8.7 10.4-4.3 1.5-10.5 1.8-17.2.7 3.7-3.1 6-6.7 6.6-10.8-4.2-2.6-7.1-7.1-7.1-12.4 0-3.7 1.4-7 3.7-9.6-1.6-2.2-2.5-4.9-2.5-7.8C24.7 17.4 33.2 8 44 8Z"
            fill="#0F172A"
            opacity="0.92"
            stroke="url(#ltHeadStroke)"
            strokeWidth="1.6"
          />

          <g clipPath="url(#ltHeadClip)">
            <rect
              x="18"
              y="14"
              width="48"
              height="52"
              rx="18"
              fill="url(#ltHeadFill)"
              style={{
                animation:
                  'lt-thinking-fill 1.7s ease-in-out infinite alternate',
              }}
            />
            <path
              d="M27 48c9-7 18-11 29-10"
              fill="none"
              stroke="#7DD3FC"
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.7"
            />
            <path
              d="M30 20c7-3 15-1 22 5"
              fill="none"
              stroke="#38BDF8"
              strokeWidth="2.6"
              strokeLinecap="round"
              opacity="0.7"
            />
          </g>

          <circle cx="39" cy="27" r="4.8" fill="#E0F2FE" opacity="0.95" />

          <g stroke="#60A5FA" strokeWidth="1.8" strokeLinecap="round">
            <line x1="9" y1="34" x2="18" y2="34" />
            <line x1="18" y1="34" x2="27" y2="26" />
            <line x1="18" y1="34" x2="28" y2="47" />
            <line x1="27" y1="26" x2="33" y2="30" />
            <line x1="28" y1="47" x2="37" y2="42" />
          </g>

          <g
            fill="#2563EB"
            style={{ animation: 'lt-thinking-glow 1.25s ease-in-out infinite' }}
          >
            <circle cx="9" cy="34" r="2.3" />
            <circle cx="18" cy="34" r="3.3" />
            <circle cx="27" cy="26" r="2.9" />
            <circle cx="28" cy="47" r="3.1" />
            <circle cx="37" cy="42" r="3.8" />
            <circle cx="33" cy="30" r="4.1" />
          </g>
        </g>
      </svg>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#93C5FD',
            lineHeight: 1.1,
          }}
        >
          Genesis
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: '#CBD5E1',
            lineHeight: 1.2,
          }}
        >
          Thinking...
        </Typography>
      </Box>
    </Box>
  );
}

function enforceMdbTheme(html) {
  let s = String(html);

  // Ensure root has data-theme
  if (!/data-theme="(dark|vibrant)"/i.test(s)) {
    s = s.replace(/^<div\b/, '<div data-theme="vibrant"');
  }

  const theme = (/data-theme="(dark|vibrant)"/i.exec(s) || ['vibrant'])[1];

  // Ensure there's at least one .card; if found, upgrade the FIRST hero card
  s = s.replace(
    /<div\b([^>]*class="[^"]*\bcard\b[^"]*"[^>]*)>/i,
    (m, attrs) => {
      if (theme === 'dark') {
        // inject dark glass + readable text on first card
        const hasStyle = /style="/i.test(attrs);
        const styleAdd =
          'border-radius:18px;background:rgba(17,25,40,.75);backdrop-filter:blur(14px);border:1px solid rgba(255,255,255,.08);';
        const newAttrs = hasStyle
          ? attrs.replace(
              /style="([^"]*)"/i,
              (mm, st) => `style="${styleAdd}${st}"`
            )
          : `${attrs} style="${styleAdd}"`;
        return `<div ${newAttrs}>`;
      } else {
        // vibrant
        const hasStyle = /style="/i.test(attrs);
        const styleAdd =
          'border-radius:18px;background:rgba(255,255,255,.78);backdrop-filter:blur(12px);';
        const newAttrs = hasStyle
          ? attrs.replace(
              /style="([^"]*)"/i,
              (mm, st) => `style="${styleAdd}${st}"`
            )
          : `${attrs} style="${styleAdd}"`;
        return `<div ${newAttrs}>`;
      }
    }
  );

  // Ensure readable text inside hero for DARK
  if (theme === 'dark') {
    // Headings to near-white, paragraphs to slate-300
    s = s
      .replace(/<h([23])\b([^>]*)>/gi, (m, lvl, attrs) => {
        if (/style="/i.test(attrs))
          return m.replace(
            /style="([^"]*)"/i,
            (mm, st) => `style="color:#f8fafc;${st}"`
          );
        return `<h${lvl} ${attrs} style="color:#f8fafc;">`;
      })
      .replace(/<p\b([^>]*)>/gi, (m, attrs) => {
        if (/style="/i.test(attrs))
          return m.replace(
            /style="([^"]*)"/i,
            (mm, st) => `style="color:#cbd5e1;${st}"`
          );
        return `<p ${attrs} style="color:#cbd5e1;">`;
      })
      .replace(
        /<div\b([^>]*)class="([^"]*card-body[^"]*)"/i,
        (m, attrs, cls) => {
          // make sure body text inherits readable color
          if (/style="/i.test(attrs))
            return m.replace(
              /style="([^"]*)"/i,
              (mm, st) => `style="color:#e6e9ef;${st}"`
            );
          return `<div ${attrs} class="${cls}" style="color:#e6e9ef;">`;
        }
      );
  }

  // Ensure the chip is 48x48 and has a gradient (both themes)
  s = s.replace(
    /(<div\b[^>]*class="[^"]*rounded-4[^"]*"[^>]*style=")([^"]*)("([^>]*)>[^<]*★)/i,
    (m, pre, style, post) => {
      const ensureSize = /width:\s*\d+px/.test(style)
        ? style
        : `width:48px;height:48px;${style}`;
      const hasGrad = /linear-gradient\(/i.test(ensureSize);
      const grad = hasGrad
        ? ''
        : 'background:linear-gradient(135deg,#4f46e5,#0ea5e9);';
      const shadow = /box-shadow:/i.test(ensureSize)
        ? ''
        : 'box-shadow:0 8px 18px rgba(14,165,233,.25);';
      const color = /color:/i.test(ensureSize) ? '' : 'color:#fff;';
      return `${pre}${grad}${shadow}${color}${ensureSize}${post}`;
    }
  );

  // Ensure primary gradient button exists for vibrant
  if (
    theme === 'vibrant' &&
    !/linear-gradient\(90deg,#4f46e5,#0ea5e9\)/i.test(s)
  ) {
    s = s.replace(
      /<a\b([^>]*class="[^"]*\bbtn\b[^"]*"[^>]*)>([^<]+)<\/a>/i,
      (m, attrs, label) => {
        const hasStyle = /style="/i.test(attrs);
        const styleAdd =
          'background:linear-gradient(90deg,#4f46e5,#0ea5e9);border:0;color:#fff;';
        const newAttrs = hasStyle
          ? attrs.replace(
              /style="([^"]*)"/i,
              (mm, st) => `style="${styleAdd}${st}"`
            )
          : `${attrs} style="${styleAdd}"`;
        return `<a ${newAttrs}>${label}</a>`;
      }
    );
  }

  return s;
}

function linkifyText(raw) {
  if (!raw) return '';
  let text = String(raw);

  // URLs with protocol
  text = text.replace(
    /((https?:\/\/)[\w\-._~:/?#[\]@!$&'()*+,;=%]+)/gi,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  // www / bare domains
  text = text.replace(
    /\b(www\.[\w\-._~:/?#[\]@!$&'()*+,;=%]+)/gi,
    '<a href="https://$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  text = text.replace(
    /\b([a-z0-9-]+(?:\.[a-z0-9-]+)+(?::\d+)?(?:\/[^\s]*)?)/gi,
    (match) => {
      if (
        /^https?:\/\//i.test(match) ||
        match.includes('@') ||
        match.includes('tel:')
      )
        return match;
      return `<a href="https://${match}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    }
  );
  // phones
  text = text.replace(
    /\b(\+?1?\s*\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4})\b/g,
    (match) => {
      const digits = match.replace(/[^\d+]/g, '');
      return `<a href="tel:${digits}">${match}</a>`;
    }
  );
  // addresses → map hook
  text = text.replace(
    /\b(\d{1,6}\s(?:[A-Za-z0-9#.\-/]+\s?)+\s(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Circle|Cir|Way|Trail|Trl|Parkway|Pkwy|Place|Pl|Square|Sq|Highway|Hwy|Crescent|Cres|Close|Terrace|Terr|Quay|Row|Walk|Grove|Hill|Parade|Vale|View|Rise|Gardens|Green|Gate)\.?,?\s*(?:[A-Za-z\s]+)?,?\s*(?:[A-Z]{2,3})?\s*(?:\d{5}(?:-\d{4})?|\d[A-Z]\d\s?\d[A-Z]\d)?)\b/g,
    (match) =>
      `<a href="#" class="chat-map-link" data-address="${encodeURIComponent(
        match.trim()
      )}">${match.trim()}</a>`
  );
  return text;
}

function markdownLinksToHtml(text) {
  // [label](url) → <a ...>
  return String(text || '').replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_, label, url) => {
      const safeUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    }
  );
}

/** ---- Memoized message bubble ---- */
const MessageItem = React.memo(function MessageItem({
  i,
  msg,
  isFirst,
  prompt,
  onMapClick,
  onGenesisAction,
  expandedMapIndex,
  expandedMapAddress,
  mapExpanded,
  setMapExpanded,
  travelMode,
  setTravelMode,
  userLocation,
}) {
  const timestamp = isFirst ? '' : msg.timestamp;
  const isUser = msg.from === 'user';

  const bubbleStyle = isUser
    ? 'bg-blue-500/20 border bg-[#161b22] border-blue-400/30 self-end'
    : 'bg-black/5 bg-[#161b22]';

  const bubbleBase =
    'backdrop-blur-md bg-[#161b22] rounded-2xl px-4 py-3 max-w-[80%] shadow-md animate-fade-in';

  return (
    <div
      className={`whitespace-pre-wrap break-words px-3 bg-[#161b22] ${
        isUser ? 'text-right' : 'text-left'
      }`}
      key={msg.id || i}
    >
      <div
        style={{ background: '#f0f3e9', color: '#253e34' }}
        className={`${bubbleBase} ${bubbleStyle} ${
          isFirst ? 'inline-block' : 'inline-block'
        }`}
      >
        {isUser ? (
          isFirst ? (
            prompt
          ) : (
            msg.text
          )
        ) : (
          <div>
            {msg.html ? (
              <div>
                <GenesisHost
                  html={msg.html}
                  onGenesisAction={onGenesisAction}
                />
                <span onClick={onMapClick} />
              </div>
            ) : (
              <span>{msg.text}</span>
            )}
            {expandedMapIndex === i && (
              <Box
                sx={{
                  mt: 2,
                  mb: 3,
                  background: '#1e1e1e',
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle2" color="#ccc">
                    Directions to: <strong>{expandedMapAddress}</strong>
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setMapExpanded(!mapExpanded)}
                    sx={{
                      fontSize: '0.75rem',
                      color: '#999',
                      borderColor: '#555',
                    }}
                    variant="outlined"
                  >
                    {mapExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                </Box>

                {mapExpanded && (
                  <>
                    <Box
                      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}
                    >
                      {['walking', 'driving', 'bicycling', 'transit'].map(
                        (mode) => (
                          <Button
                            key={mode}
                            size="small"
                            variant={
                              travelMode === mode ? 'contained' : 'outlined'
                            }
                            onClick={() => setTravelMode(mode)}
                            sx={{
                              textTransform: 'capitalize',
                              color: '#ddd',
                              borderColor: '#666',
                              background:
                                travelMode === mode ? '#2563eb' : 'transparent',
                            }}
                          >
                            {mode}
                          </Button>
                        )
                      )}
                    </Box>

                    <iframe
                      width="100%"
                      height="250"
                      style={{
                        border: 0,
                        borderRadius: '12px',
                        marginTop: '10px',
                      }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/directions?key=${
                        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
                      }&origin=${encodeURIComponent(
                        userLocation || ''
                      )}&destination=${encodeURIComponent(
                        expandedMapAddress || ''
                      )}&mode=${travelMode}`}
                      title="Directions"
                    />

                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(
                          expandedMapAddress || ''
                        )}`}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/c/cc/Uber_logo_2018.png"
                          alt="Uber"
                          style={{ height: 20, marginRight: 8 }}
                        />
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://ride.lyft.com/?destination=${encodeURIComponent(
                          expandedMapAddress || ''
                        )}`}
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Lyft_logo.svg/398px-Lyft_logo.svg.png"
                          alt="Lyft"
                          style={{ height: 20, marginRight: 8 }}
                        />
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </div>
        )}
        {timestamp && (
          <div className="text-xs mt-1" style={{ color: '#435748' }}>
            {timestamp}
          </div>
        )}
      </div>
    </div>
  );
});

export default function AIChatBot({ chatbotId, preText, embedded = false }) {
  const [prompt, setPrompt] = useState(
    preText || 'Hello! How can I assist you today?'
  );
  const [travelMode, setTravelMode] = useState('walking');
  const [mapExpanded, setMapExpanded] = useState(true);
  const [expandedMap, setExpandedMap] = useState({ address: '', index: null });
  const [userLocation, setUserLocation] = useState(null);
  const [context, setContext] = useState(null);
  const [messages, setMessages] = useState(() => [
    {
      id: 'welcome',
      from: 'bot',
      text: preText || 'Hello! How can I assist you today?',
      html: null,
      timestamp: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      }),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechError, setSpeechError] = useState('');
  const bottomRef = useRef(null);
  const conversationRef = useRef(null);
  if (!conversationRef.current || conversationRef.current.botId !== chatbotId) {
    conversationRef.current = {
      botId: chatbotId,
      id: window.crypto.randomUUID(),
    };
  }
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const shouldSubmitVoiceRef = useRef(false);
  const SpeechRecognition =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const speechSupported = Boolean(SpeechRecognition);

  /** Auto-scroll only when messages or loading change */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
      }
    };
  }, []);

  /** Geolocation + context (single effect) */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const coords = await new Promise((resolve, reject) => {
          if (!navigator.geolocation)
            return reject(new Error('Geolocation not supported'));
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos.coords),
            (err) => reject(err),
            { timeout: 5000 }
          );
        });
        if (!mounted) return;
        const locStr = `${coords.latitude},${coords.longitude}`;
        setUserLocation(locStr);

        let location = 'Unknown location';
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.latitude}&lon=${coords.longitude}`,
            { headers: { 'User-Agent': 'LifeTaggerBot/1.0' } }
          );
          const data = await res.json();
          location = data.address?.city
            ? `${data.address.city}, ${data.address.state}`
            : data.display_name || location;
        } catch {
          console.log('Failed to reverse geocode');
        }

        const currentUser = Parse.User.current();
        const timestamp = new Date().toLocaleString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        const timezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
        const language = navigator.language || 'en-US';
        const ua = navigator.userAgent;
        const deviceType = /Mobi|Android/i.test(ua) ? 'Mobile' : 'Desktop';
        const browser = /Chrome/i.test(ua)
          ? 'Chrome'
          : /Safari/i.test(ua)
          ? 'Safari'
          : /Firefox/i.test(ua)
          ? 'Firefox'
          : 'Other';
        const os = /Win/i.test(ua)
          ? 'Windows'
          : /Mac/i.test(ua)
          ? 'MacOS'
          : /Linux/i.test(ua)
          ? 'Linux'
          : /Android/i.test(ua)
          ? 'Android'
          : /iPhone|iPad/i.test(ua)
          ? 'iOS'
          : 'Other';

        const sensitiveKeys = ['token', 'auth', 'session', 'jwt'];
        const cookies = document.cookie
          .split(';')
          .map((c) => c.trim().split('='))
          .reduce((acc, [key, val]) => {
            if (!key) return acc;
            const lowerKey = key.toLowerCase();
            const isSensitive = sensitiveKeys.some((s) => lowerKey.includes(s));
            acc[key] = isSensitive
              ? '[REDACTED]'
              : decodeURIComponent(val || '');
            return acc;
          }, {});

        setContext({
          location,
          gps: { latitude: coords.latitude, longitude: coords.longitude },
          timestamp,
          timezone,
          language,
          userName: currentUser?.get('name') || 'Guest',
          email: currentUser?.get('email') || 'N/A',
          deviceType,
          browser,
          cookies,
          os,
        });
      } catch {
        // fine – context remains partial
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  /** Optional: translate initial prompt once if non-English */
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (navigator.language && !navigator.language.startsWith('en')) {
        try {
          const translated = await Parse.Cloud.run('translatePrompt', {
            text: prompt,
            targetLang: navigator.language || 'en',
          });
          if (mounted) setPrompt(translated);
        } catch {
          console.log('Translation failed; using original prompt');
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []); // once

  /** Stable handler: map click (event delegation) */
  // const onMapClick = useCallback((e) => {
  //   const target = e.target;
  //   if (target && target.dataset && target.dataset.address) {
  //     const address = decodeURIComponent(target.dataset.address);
  //     // The MessageItem receives its own index via props; we’ll track expandedMap via message index in parent rendering loop
  //     // Here, we find the closest message wrapper by data-index if needed (but we pass index in props instead)
  //   }
  // }, []);

  /** Build sanitized HTML for bot responses exactly once */
  const buildBotHtml = useCallback((raw) => {
    // If the model returned HTML (has a tag), clean it; otherwise linkify and wrap.
    const looksLikeHtml = /<\w+[^>]*>/.test(raw || '');
    // Preserve the designer's composition. GenesisHost sanitizes and isolates it.
    if (looksLikeHtml)
      return String(raw)
        .replace(/^\s*```(?:html)?\s*/i, '')
        .replace(/\s*```\s*$/i, '');
    let html = markdownLinksToHtml(linkifyText(raw));
    let htmlMDB = enforceMdbTheme(html);
    console.log('htmlMDB', htmlMDB);
    return `<div>${html}</div>`;
  }, []);

  const submitMessage = useCallback(
    async ({ messageText, localContext = null, source = 'user' }) => {
      const trimmedMessage = String(messageText || '').trim();
      if (!trimmedMessage || loading) return;

      const historic = messages.slice(-6);
      const now = new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });
      const userMessage = {
        id: `u-${Date.now()}`,
        from: 'user',
        text: trimmedMessage,
        html: null,
        timestamp: now,
        source,
      };
      const requestContext =
        source === 'genesis_action'
          ? {
              ...(context || {}),
              genesisAction: {
                type: 'genesis_prompt',
                prompt: trimmedMessage,
                local: localContext,
              },
            }
          : context;

      setMessages((prev) => [...prev, userMessage]);
      if (source === 'user') {
        setInput('');
      }
      setLoading(true);

      try {
        const response = await Parse.Cloud.run('chatbotQuery', {
          historicMessages: historic,
          message: userMessage.text,
          chatBotId: chatbotId,
          conversationId: conversationRef.current.id,
          context: requestContext,
        });

        const reply =
          response?.answer ?? response ?? 'Sorry, I didn’t catch that.';
        const html = buildBotHtml(reply);
        const botMessage = {
          id: `b-${Date.now()}`,
          from: 'bot',
          text: reply,
          html,
          timestamp: new Date().toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          }),
        };

        setMessages((prev) => [...prev, botMessage]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            from: 'bot',
            text: 'Error talking to the assistant.',
            html: null,
            timestamp: new Date().toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
            }),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [buildBotHtml, chatbotId, context, loading, messages]
  );

  /** Send message: sanitize ONCE, then store html in message */
  const sendMessage = useCallback(async () => {
    if (!input.trim()) return;
    await submitMessage({ messageText: input, source: 'user' });
  }, [input, submitMessage]);

  const handleGenesisAction = useCallback(
    async ({ prompt: actionPrompt, local }) => {
      if (!actionPrompt || loading) return;
      await submitMessage({
        messageText: actionPrompt,
        localContext: local,
        source: 'genesis_action',
      });
    },
    [loading, submitMessage]
  );

  const stopListening = useCallback(() => {
    shouldSubmitVoiceRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!speechSupported || loading) {
      return;
    }

    setSpeechError('');
    finalTranscriptRef.current = '';
    shouldSubmitVoiceRef.current = true;

    if (!recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.lang = navigator.language || 'en-US';
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = finalTranscriptRef.current;

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const transcript = event.results[i][0]?.transcript || '';
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        finalTranscriptRef.current = finalTranscript.trim();
        setInput(`${finalTranscript}${interimTranscript}`.trim());
      };

      recognition.onerror = (event) => {
        if (event.error === 'no-speech') {
          setSpeechError('No speech detected. Please try again.');
        } else if (event.error === 'not-allowed') {
          setSpeechError('Microphone access was blocked.');
        } else {
          setSpeechError('Voice input is unavailable right now.');
        }
        shouldSubmitVoiceRef.current = false;
        setIsListening(false);
      };

      recognition.onend = async () => {
        setIsListening(false);
        const spokenMessage = finalTranscriptRef.current.trim();
        finalTranscriptRef.current = '';

        if (!shouldSubmitVoiceRef.current || !spokenMessage) {
          return;
        }

        shouldSubmitVoiceRef.current = false;
        await submitMessage({
          messageText: spokenMessage,
          source: 'voice',
        });
      };

      recognitionRef.current = recognition;
    }

    try {
      recognitionRef.current.lang = navigator.language || 'en-US';
      recognitionRef.current.start();
      setIsListening(true);
    } catch (error) {
      setSpeechError('Voice input could not be started.');
      setIsListening(false);
      shouldSubmitVoiceRef.current = false;
    }
  }, [SpeechRecognition, loading, speechSupported, submitMessage]);

  const handleVoiceToggle = useCallback(() => {
    if (isListening) {
      stopListening();
      return;
    }

    startListening();
  }, [isListening, startListening, stopListening]);

  /** Stable keydown handler */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  /** Stable per-message map click that sets expandedMap by index */
  const handleBubbleClick = useCallback((e, index) => {
    const target = e.target;
    if (target && target.dataset && target.dataset.address) {
      const address = decodeURIComponent(target.dataset.address);
      setExpandedMap({ address, index });
    }
  }, []);

  return (
    <div
      className={`lt-lifebot-chat ${
        embedded ? 'lt-lifebot-embedded' : 'fixed inset-0'
      } flex flex-col text-base font-sans`}
    >
      <header className="lt-lifebot-chat-heading">
        <span>LifeBot</span>
        <strong>A conversation that does more.</strong>
        <small>Ask a question or explore your connected tools.</small>
      </header>
      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto space-y-4 px-6 pt-6 pb-3">
        {messages.map((msg, i) => (
          <MessageItem
            key={msg.id || i}
            i={i}
            msg={msg}
            isFirst={i === 0}
            prompt={prompt}
            onMapClick={(e) => handleBubbleClick(e, i)}
            onGenesisAction={handleGenesisAction}
            expandedMapIndex={expandedMap.index}
            expandedMapAddress={expandedMap.address}
            mapExpanded={mapExpanded}
            setMapExpanded={setMapExpanded}
            travelMode={travelMode}
            setTravelMode={setTravelMode}
            userLocation={userLocation}
          />
        ))}

        {/* Typing animation */}
        {loading && (
          <div className="text-left">
            <ThinkingLoader />
          </div>
        )}

        {isListening && !loading && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              ml: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: '999px',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(248, 113, 113, 0.35)',
              color: '#FCA5A5',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#EF4444',
                animation: 'blink 1.2s infinite',
              }}
            />
            Listening...
          </Box>
        )}

        {speechError && !loading && (
          <Typography sx={{ ml: 1, color: '#FCA5A5', fontSize: 12 }}>
            {speechError}
          </Typography>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input area at bottom */}
      <div
        className="sticky bottom-0 left-0 right-0 px-4 py-4 flex items-center gap-3 border-t border-gray-700 z-10"
        style={{ background: '#f5f6ef' }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <TextField
          className="flex-1 bg-[#0d1117] text-white"
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          InputProps={{
            style: { color: '#253e34', backgroundColor: '#fffefa' },
            sx: {
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#58a6ff',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#58a6ff',
              },
            },
          }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question..."
          disabled={loading || isListening}
        />
        {speechSupported && (
          <Tooltip title={isListening ? 'Stop listening' : 'Speak to Genesis'}>
            <span>
              <IconButton
                onClick={handleVoiceToggle}
                disabled={loading}
                sx={{
                  color: isListening ? '#FCA5A5' : '#93C5FD',
                  border: '1px solid',
                  borderColor: isListening
                    ? 'rgba(248, 113, 113, 0.4)'
                    : 'rgba(59, 130, 246, 0.35)',
                  backgroundColor: isListening
                    ? 'rgba(127, 29, 29, 0.22)'
                    : 'rgba(15, 23, 42, 0.9)',
                  borderRadius: '14px',
                  '&:hover': {
                    backgroundColor: isListening
                      ? 'rgba(127, 29, 29, 0.32)'
                      : 'rgba(30, 41, 59, 0.96)',
                  },
                }}
              >
                {isListening ? <StopRoundedIcon /> : <MicRoundedIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Button
          onClick={sendMessage}
          disabled={loading || isListening}
          variant="contained"
          sx={{
            background: '#294b36',
            color: 'white',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              background: '#365c43',
            },
          }}
        >
          Send
        </Button>
      </div>

      <style>{`
        @keyframes blink {
          0% { opacity: 0.2 }
          20% { opacity: 1 }
          100% { opacity: 0.2 }
        }
        .animate-blink { animation: blink 1.4s infinite; }
        .delay-100 { animation-delay: 0.2s; }
        .delay-200 { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
}
